import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { toast } from 'sonner';
import { ErrorService, resolveAuthError } from '@/lib/errors';
import type { ApiError } from '@/lib/errors';

export interface PendingInvitation {
  id: string;
  target_cabinet: {
    id: string;
    name: string;
    member_count: number;
  };
  current_cabinet: {
    id: string;
    name: string;
    member_count: number;
  };
  invited_by: {
    id: string;
    name: string;
    email: string;
  };
  created_at: string;
  expires_at: string;
}

export const usePendingInvitations = () => {
  return useQuery({
    queryKey: ['pending-invitations'],
    queryFn: async () => {
      console.log('[usePendingInvitations] Fetching pending invitations...');
      // Note: apiClient.get() returns response.data directly from ApiResponse<T>
      // So if backend returns { success: true, data: [...] }, apiClient returns [...]
      const response = await apiClient.get<PendingInvitation[]>(
        '/cabinet-invitations/pending'
      );
      console.log('[usePendingInvitations] Response received:', response);
      console.log('[usePendingInvitations] Is array:', Array.isArray(response));
      console.log('[usePendingInvitations] Length:', response?.length);
      // response is already the array of invitations
      return response || [];
    },
    refetchInterval: 30000,
    // Le polling 30s capte déjà les nouvelles invitations ; pas de refetch au
    // focus (évite un appel + re-render parasite à chaque retour de fenêtre, #238).
    refetchOnWindowFocus: false
  });
};

export const useAcceptInvitation = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (invitationId: string) => {
      // Note: apiClient.post() returns response.data directly
      const response = await apiClient.post<{ new_cabinet_id: string; previous_deleted: boolean }>(
        `/cabinet-invitations/${invitationId}/accept`
      );
      return response;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['pending-invitations'] });
      queryClient.invalidateQueries({ queryKey: ['cabinets'] });
      queryClient.invalidateQueries({ queryKey: ['cabinet-members'] });
      
      toast.success(data.previous_deleted
        ? "Cabinet rejoint. Votre ancien cabinet a été supprimé car vous étiez le seul membre."
        : "Vous avez rejoint votre nouveau cabinet avec succès.");
      
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
    onError: (error: unknown) => {
      // Map via resolveAuthError so domain-specific codes (INVITATION_EXPIRED, NO_LICENSES_AVAILABLE,
      // etc.) produce a precise FR message instead of a fixed generic string.
      // ApiError.message = readable FR text from backend (set by buildApiError in apiClient.ts).
      const a = error as Partial<ApiError>;
      const { toastMessage } = resolveAuthError({
        errorCode: a.errorCode,
        backendMessage: a.message,
        requestId: a.requestId,
        httpStatus: a.statusCode,
        // WHY: pass details so Zod field issues (details.issues) produce precise FR
        // messages via resolveAuthError §4.3. No form here → only toastMessage consumed.
        details: a.details as { issues?: Array<{ path: Array<string | number>; message: string }> } | undefined,
      });
      ErrorService.handle(
        error instanceof Error ? error : new Error("Impossible d'accepter l'invitation"),
        { userMessage: toastMessage ?? "L'enregistrement n'a pas abouti. Veuillez réessayer.", context: { action: 'accept_invitation' } },
      );
    }
  });
};

export const useDeclineInvitation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invitationId: string) => {
      await apiClient.post(`/cabinet-invitations/${invitationId}/decline`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pending-invitations'] });
      toast.success("Invitation refusée. Vous restez dans votre cabinet actuel.");
    },
    onError: (error: unknown) => {
      // Map via resolveAuthError so domain-specific codes (INVITATION_EXPIRED, etc.) produce a
      // precise FR message instead of a fixed generic string.
      const a = error as Partial<ApiError>;
      const { toastMessage } = resolveAuthError({
        errorCode: a.errorCode,
        backendMessage: a.message,
        requestId: a.requestId,
        httpStatus: a.statusCode,
        // WHY: pass details so Zod field issues produce precise FR messages. No form here.
        details: a.details as { issues?: Array<{ path: Array<string | number>; message: string }> } | undefined,
      });
      ErrorService.handle(
        error instanceof Error ? error : new Error("Impossible de refuser l'invitation"),
        { userMessage: toastMessage ?? "L'enregistrement n'a pas abouti. Veuillez réessayer.", context: { action: 'decline_invitation' } },
      );
    }
  });
};
